<template>
  <div class="space-y-6">
    <!-- Sub-navigation -->
    <div class="flex flex-wrap gap-2 border-b border-gray-200 pb-4">
      <button
        v-for="tab in subTabs"
        :key="tab.id"
        @click="activeSubTab = tab.id"
        :class="[
          'px-4 py-2 rounded-lg text-sm font-medium transition-all',
          activeSubTab === tab.id
            ? 'bg-black text-white'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        ]"
      >
        {{ tab.label }}
        <span v-if="tab.count !== undefined" class="ml-1 text-xs opacity-75">({{ tab.count }})</span>
      </button>
    </div>

    <!-- APPLICATIONS VIEW -->
    <div v-if="activeSubTab === 'applications'">
      <!-- Filters + Add button -->
      <div class="flex flex-wrap items-center gap-4 mb-6">
        <button
          @click="showManualAppForm = true"
          class="px-4 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
        >
          + Add Application
        </button>
        <select v-model="filterJob" @change="loadApplications" class="rounded-lg border-gray-300 text-sm">
          <option value="">All Jobs</option>
          <option v-for="job in jobs" :key="job._id" :value="job.jobId">{{ job.title }}</option>
        </select>
        <select v-model="filterStatus" @change="loadApplications" class="rounded-lg border-gray-300 text-sm">
          <option value="">All Statuses</option>
          <option value="new">New</option>
          <option value="shortlisted">Shortlisted</option>
          <option value="interview">Interview</option>
          <option value="employed">Employed</option>
          <option value="rejected">Rejected</option>
        </select>
        <input
          v-model="searchQuery"
          @input="debouncedSearch"
          type="text"
          placeholder="Search name, email, phone..."
          class="rounded-lg border-gray-300 text-sm w-64"
        />
      </div>

      <!-- Stats bar -->
      <div class="grid grid-cols-5 gap-3 mb-6">
        <div v-for="(count, status) in applicationStats" :key="status"
          class="bg-white border rounded-lg p-3 text-center cursor-pointer hover:shadow-md transition-shadow"
          :class="filterStatus === status ? 'ring-2 ring-black' : ''"
          @click="filterStatus = status; loadApplications()"
        >
          <div class="text-2xl font-bold" :class="statusColor(status)">{{ count }}</div>
          <div class="text-xs text-gray-500 capitalize">{{ status }}</div>
        </div>
      </div>

      <!-- Applications list -->
      <div v-if="loading" class="text-center py-8 text-gray-500">Loading applications...</div>
      <div v-else-if="applications.length === 0" class="text-center py-8 text-gray-500">No applications found</div>
      <div v-else class="space-y-3">
        <div
          v-for="app in applications"
          :key="app._id"
          class="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
          :class="selectedApp?._id === app._id ? 'ring-2 ring-black' : ''"
          @click="selectApplication(app)"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <span class="inline-block px-2 py-1 rounded-full text-xs font-medium" :class="statusBadgeClass(app.status)">
                {{ app.status }}
              </span>
              <div>
                <span class="font-medium">{{ app.name }}</span>
                <span class="text-gray-500 text-sm ml-2">{{ app.email }}</span>
              </div>
            </div>
            <div class="text-sm text-gray-400">
              {{ formatDate(app.submittedAt) }}
            </div>
          </div>
          <div class="mt-1 text-sm text-gray-500">
            {{ app.jobTitle }} &middot; {{ app.phone }}
            <span v-if="app.location"> &middot; {{ app.location }}</span>
            <span v-if="app.source === 'manual'" class="ml-1 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">manual</span>
          </div>
        </div>
      </div>

      <!-- Application Detail Modal -->
      <div v-if="selectedApp" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50" @click.self="selectedApp = null">
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4">
          <div class="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <h3 class="text-lg font-bold">{{ selectedApp.name }}</h3>
            <button @click="selectedApp = null" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
          <div class="p-6 space-y-6">
            <!-- Status -->
            <div class="flex items-center gap-3">
              <label class="text-sm font-medium text-gray-500">Status:</label>
              <select
                :value="selectedApp.status"
                @change="updateApplicationStatus(selectedApp, $event.target.value)"
                class="rounded-lg border-gray-300 text-sm font-medium"
              >
                <option value="new">New</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="interview">Interview</option>
                <option value="employed">Employed</option>
                <option value="rejected">Rejected</option>
              </select>
              <span class="inline-block px-2 py-1 rounded-full text-xs font-medium" :class="statusBadgeClass(selectedApp.status)">
                {{ selectedApp.status }}
              </span>
            </div>

            <!-- Contact info -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="text-xs text-gray-500 uppercase">Email</label>
                <a :href="'mailto:' + selectedApp.email" class="block text-blue-600 hover:underline">{{ selectedApp.email }}</a>
              </div>
              <div>
                <label class="text-xs text-gray-500 uppercase">Phone</label>
                <a :href="'tel:' + selectedApp.phone" class="block text-blue-600 hover:underline">{{ selectedApp.phone }}</a>
              </div>
              <div>
                <label class="text-xs text-gray-500 uppercase">Location</label>
                <p>{{ selectedApp.location || 'Not specified' }}</p>
              </div>
              <div>
                <label class="text-xs text-gray-500 uppercase">Languages</label>
                <p>{{ selectedApp.languages }}</p>
              </div>
              <div>
                <label class="text-xs text-gray-500 uppercase">Licence</label>
                <p>{{ selectedApp.licence || 'Not specified' }}</p>
              </div>
              <div>
                <label class="text-xs text-gray-500 uppercase">Applied for</label>
                <p>{{ selectedApp.jobTitle }}</p>
              </div>
            </div>

            <!-- Qualifications -->
            <div>
              <label class="text-xs text-gray-500 uppercase">Qualifications / Experience</label>
              <p class="whitespace-pre-wrap mt-1 p-3 bg-gray-50 rounded-lg">{{ selectedApp.qualifications }}</p>
            </div>

            <!-- Motivation -->
            <div v-if="selectedApp.motivation">
              <label class="text-xs text-gray-500 uppercase">Motivation</label>
              <p class="whitespace-pre-wrap mt-1 p-3 bg-gray-50 rounded-lg">{{ selectedApp.motivation }}</p>
            </div>

            <!-- Reference -->
            <div v-if="selectedApp.reference">
              <label class="text-xs text-gray-500 uppercase">Reference</label>
              <p class="mt-1">{{ selectedApp.reference }}</p>
            </div>

            <!-- Attached files -->
            <div v-if="selectedApp.fileNames && selectedApp.fileNames.length > 0">
              <label class="text-xs text-gray-500 uppercase">Attached Documents</label>
              <div class="flex flex-wrap gap-2 mt-1">
                <span v-for="f in selectedApp.fileNames" :key="f" class="px-3 py-1 bg-gray-100 rounded text-sm">{{ f }}</span>
              </div>
              <p class="text-xs text-gray-400 mt-1">Files were sent to hr@83islands.com via email</p>
            </div>

            <!-- Notes -->
            <div>
              <label class="text-xs text-gray-500 uppercase">Admin Notes</label>
              <textarea
                v-model="selectedApp.notes"
                @blur="saveNotes(selectedApp)"
                rows="3"
                placeholder="Add internal notes about this candidate..."
                class="mt-1 w-full rounded-lg border-gray-300 text-sm"
              ></textarea>
            </div>

            <!-- Status History -->
            <div v-if="selectedApp.statusHistory && selectedApp.statusHistory.length > 0">
              <label class="text-xs text-gray-500 uppercase">Status History</label>
              <div class="mt-1 space-y-1">
                <div v-for="(entry, i) in selectedApp.statusHistory" :key="i" class="flex items-center gap-2 text-sm">
                  <span class="inline-block px-2 py-0.5 rounded-full text-xs" :class="statusBadgeClass(entry.status)">{{ entry.status }}</span>
                  <span class="text-gray-400">{{ formatDate(entry.date) }}</span>
                  <span v-if="entry.note" class="text-gray-600">— {{ entry.note }}</span>
                </div>
              </div>
            </div>

            <!-- Action buttons -->
            <div class="flex flex-wrap gap-3 pt-4 border-t">
              <button
                v-if="selectedApp.status === 'new' || selectedApp.status === 'shortlisted'"
                @click="showInterviewModal = true"
                class="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Schedule Interview
              </button>
              <button
                v-if="selectedApp.status !== 'rejected'"
                @click="updateApplicationStatus(selectedApp, 'rejected')"
                class="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
              >
                Reject
              </button>
              <button
                v-if="selectedApp.status === 'new'"
                @click="updateApplicationStatus(selectedApp, 'shortlisted')"
                class="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg text-sm font-medium hover:bg-yellow-200 transition-colors"
              >
                Shortlist
              </button>
              <button
                v-if="selectedApp.status === 'interview'"
                @click="markEmployedAndClose(selectedApp)"
                class="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
              >
                Mark as Employed & Close Position
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Interview Scheduling Modal -->
      <div v-if="showInterviewModal" class="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" @click.self="showInterviewModal = false">
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-md m-4 p-6">
          <h3 class="text-lg font-bold mb-4">Schedule Interview for {{ selectedApp?.name }}</h3>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Date *</label>
              <input type="date" v-model="interviewForm.date" class="mt-1 w-full rounded-lg border-gray-300 text-sm" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Time *</label>
              <input type="time" v-model="interviewForm.time" class="mt-1 w-full rounded-lg border-gray-300 text-sm" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Location</label>
              <input type="text" v-model="interviewForm.location" placeholder="e.g., 83 Islands Distillery, Stella Mare" class="mt-1 w-full rounded-lg border-gray-300 text-sm" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Additional message to candidate</label>
              <textarea v-model="interviewForm.customMessage" rows="2" placeholder="e.g., Please bring your ID..." class="mt-1 w-full rounded-lg border-gray-300 text-sm"></textarea>
            </div>
            <div class="flex gap-3 pt-2">
              <button
                @click="sendInterviewInvite"
                :disabled="!interviewForm.date || !interviewForm.time || sendingInterview"
                class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {{ sendingInterview ? 'Sending...' : 'Send Interview Invitation' }}
              </button>
              <button @click="showInterviewModal = false" class="px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Manual Application Modal -->
      <div v-if="showManualAppForm" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50" @click.self="showManualAppForm = false">
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4 p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-bold">Add Manual Application</h3>
            <button @click="showManualAppForm = false" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Position *</label>
              <select v-model="manualAppForm.jobId" @change="onManualJobSelect" class="mt-1 w-full rounded-lg border-gray-300 text-sm">
                <option value="">Select a job</option>
                <option v-for="job in jobs" :key="job._id" :value="job.jobId">{{ job.title }}</option>
                <option value="_other">Other (type manually)</option>
              </select>
              <input v-if="manualAppForm.jobId === '_other'" type="text" v-model="manualAppForm.jobTitle" placeholder="Job title" class="mt-2 w-full rounded-lg border-gray-300 text-sm" />
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Name *</label>
                <input type="text" v-model="manualAppForm.name" class="mt-1 w-full rounded-lg border-gray-300 text-sm" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Email *</label>
                <input type="email" v-model="manualAppForm.email" class="mt-1 w-full rounded-lg border-gray-300 text-sm" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Phone</label>
                <input type="tel" v-model="manualAppForm.phone" class="mt-1 w-full rounded-lg border-gray-300 text-sm" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Neighbourhood</label>
                <input type="text" v-model="manualAppForm.location" class="mt-1 w-full rounded-lg border-gray-300 text-sm" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Languages</label>
                <input type="text" v-model="manualAppForm.languages" class="mt-1 w-full rounded-lg border-gray-300 text-sm" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Licence</label>
                <input type="text" v-model="manualAppForm.licence" class="mt-1 w-full rounded-lg border-gray-300 text-sm" />
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Qualifications / Experience</label>
              <textarea v-model="manualAppForm.qualifications" rows="3" class="mt-1 w-full rounded-lg border-gray-300 text-sm"></textarea>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Motivation</label>
              <textarea v-model="manualAppForm.motivation" rows="2" class="mt-1 w-full rounded-lg border-gray-300 text-sm"></textarea>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Reference</label>
              <input type="text" v-model="manualAppForm.reference" class="mt-1 w-full rounded-lg border-gray-300 text-sm" />
            </div>
            <div class="flex gap-3 pt-2">
              <button
                @click="submitManualApplication"
                :disabled="!manualAppForm.name || !manualAppForm.email || (!manualAppForm.jobId || (manualAppForm.jobId === '_other' && !manualAppForm.jobTitle)) || savingManualApp"
                class="flex-1 px-4 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors"
              >
                {{ savingManualApp ? 'Saving...' : 'Add Application' }}
              </button>
              <button @click="showManualAppForm = false" class="px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MANAGE JOBS VIEW -->
    <div v-if="activeSubTab === 'jobs'">
      <div class="flex justify-between items-center mb-6">
        <h3 class="text-lg font-bold">Job Listings</h3>
        <button @click="showJobForm = !showJobForm" class="px-4 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">
          {{ showJobForm ? 'Cancel' : '+ New Job' }}
        </button>
      </div>

      <!-- New Job Form -->
      <div v-if="showJobForm" class="border rounded-lg p-6 mb-6 bg-gray-50">
        <h4 class="font-bold mb-4">{{ editingJob ? 'Edit Job' : 'Create New Job' }}</h4>
        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Title *</label>
              <input type="text" v-model="jobForm.title" class="mt-1 w-full rounded-lg border-gray-300 text-sm" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Location</label>
              <input type="text" v-model="jobForm.location" placeholder="e.g., Stella Mare, Efate" class="mt-1 w-full rounded-lg border-gray-300 text-sm" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Type</label>
              <select v-model="jobForm.type" class="mt-1 w-full rounded-lg border-gray-300 text-sm">
                <option>Full-time</option>
                <option>Part-time</option>
                <option>Contract</option>
                <option>Internship</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Application Deadline</label>
              <input type="text" v-model="jobForm.deadline" placeholder="e.g., 31 March 2026" class="mt-1 w-full rounded-lg border-gray-300 text-sm" />
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">Summary</label>
            <textarea v-model="jobForm.summary" rows="2" class="mt-1 w-full rounded-lg border-gray-300 text-sm"></textarea>
          </div>

          <!-- Sections -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-gray-700">Sections</label>
              <button @click="addSection" type="button" class="text-sm text-blue-600 hover:text-blue-800">+ Add Section</button>
            </div>
            <div v-for="(section, sIdx) in jobForm.sections" :key="sIdx" class="border rounded-lg p-4 mb-3 bg-white">
              <div class="flex items-center gap-2 mb-2">
                <input type="text" v-model="section.title" placeholder="Section title" class="flex-1 rounded-lg border-gray-300 text-sm" />
                <button @click="jobForm.sections.splice(sIdx, 1)" type="button" class="text-red-400 hover:text-red-600">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                </button>
              </div>
              <textarea
                v-model="section.itemsText"
                rows="3"
                placeholder="One item per line"
                class="w-full rounded-lg border-gray-300 text-sm"
              ></textarea>
            </div>
          </div>

          <div class="flex gap-3">
            <button
              @click="saveJob"
              :disabled="!jobForm.title || savingJob"
              class="px-6 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors"
            >
              {{ savingJob ? 'Saving...' : (editingJob ? 'Update Job' : 'Create Job & Notify Future Candidates') }}
            </button>
          </div>
          <p v-if="!editingJob && futureCandidatesCount > 0" class="text-sm text-blue-600">
            {{ futureCandidatesCount }} future candidate(s) will be automatically notified by email
          </p>
        </div>
      </div>

      <!-- Jobs List -->
      <div v-if="loadingJobs" class="text-center py-8 text-gray-500">Loading jobs...</div>
      <div v-else-if="jobs.length === 0 && !showJobForm" class="text-center py-8 text-gray-500">
        No jobs created yet. Click "+ New Job" to create one.
      </div>
      <div v-else class="space-y-3">
        <div v-for="job in jobs" :key="job._id" class="border rounded-lg p-4 hover:shadow-md transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <div class="flex items-center gap-2">
                <h4 class="font-bold">{{ job.title }}</h4>
                <span :class="job.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'" class="px-2 py-0.5 rounded-full text-xs font-medium">
                  {{ job.active ? 'Active' : 'Closed' }}
                </span>
              </div>
              <div class="text-sm text-gray-500 mt-1">
                {{ job.location }} &middot; {{ job.type }}
                <span v-if="job.deadline"> &middot; Deadline: {{ job.deadline }}</span>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <button @click="editJob(job)" class="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
              </button>
              <button @click="toggleJobActive(job)" class="p-2 text-gray-400 hover:text-gray-600 transition-colors" :title="job.active ? 'Close job' : 'Reopen job'">
                <svg v-if="job.active" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/></svg>
                <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- FUTURE CANDIDATES VIEW -->
    <div v-if="activeSubTab === 'future'">
      <div class="mb-6">
        <h3 class="text-lg font-bold">Future Candidates</h3>
        <p class="text-sm text-gray-500 mt-1">People who opted in to be notified about new job openings. They will automatically receive an email when you create a new job.</p>
      </div>

      <div v-if="loadingFuture" class="text-center py-8 text-gray-500">Loading...</div>
      <div v-else-if="futureCandidates.length === 0" class="text-center py-8 text-gray-500">
        No candidates have opted in yet. When you close a position, all non-hired candidates will be invited to join this list.
      </div>
      <div v-else>
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b">
              <th class="text-left py-2 px-3 font-medium text-gray-500">Name</th>
              <th class="text-left py-2 px-3 font-medium text-gray-500">Email</th>
              <th class="text-left py-2 px-3 font-medium text-gray-500">Opted In</th>
              <th class="text-left py-2 px-3 font-medium text-gray-500">Source</th>
              <th class="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="candidate in futureCandidates" :key="candidate._id" class="border-b hover:bg-gray-50">
              <td class="py-2 px-3">{{ candidate.name }}</td>
              <td class="py-2 px-3"><a :href="'mailto:' + candidate.email" class="text-blue-600 hover:underline">{{ candidate.email }}</a></td>
              <td class="py-2 px-3">{{ formatDate(candidate.optedInAt) }}</td>
              <td class="py-2 px-3 text-gray-500">{{ candidate.source }}</td>
              <td class="py-2 px-3">
                <button @click="removeFutureCandidate(candidate)" class="text-red-400 hover:text-red-600 text-xs">Remove</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Toast notifications -->
    <div v-if="toast" class="fixed bottom-4 right-4 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm font-medium animate-fade-in"
      :class="toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'"
    >
      {{ toast.message }}
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'

const api = axios.create({
  baseURL: import.meta.env.DEV ? '' : import.meta.env.API_URL?.replace(/\/+$/, ''),
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
})

// Add auth header to all requests
api.interceptors.request.use(config => {
  const token = localStorage.getItem('admin_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// State
const activeSubTab = ref('applications')
const loading = ref(false)
const loadingJobs = ref(false)
const loadingFuture = ref(false)
const applications = ref([])
const applicationStats = ref({ new: 0, shortlisted: 0, interview: 0, employed: 0, rejected: 0 })
const jobs = ref([])
const futureCandidates = ref([])
const selectedApp = ref(null)
const filterJob = ref('')
const filterStatus = ref('')
const searchQuery = ref('')
const toast = ref(null)

// Interview modal
const showInterviewModal = ref(false)
const sendingInterview = ref(false)
const interviewForm = ref({ date: '', time: '', location: '', customMessage: '' })

// Manual application form
const showManualAppForm = ref(false)
const savingManualApp = ref(false)
const manualAppForm = ref({ jobId: '', jobTitle: '', name: '', email: '', phone: '', location: '', languages: '', qualifications: '', licence: '', motivation: '', reference: '' })

// Job form
const showJobForm = ref(false)
const savingJob = ref(false)
const editingJob = ref(null)
const jobForm = ref({ title: '', location: '', type: 'Full-time', deadline: '', summary: '', sections: [] })

const subTabs = computed(() => [
  { id: 'applications', label: 'Applications', count: applications.value.length },
  { id: 'jobs', label: 'Manage Jobs', count: jobs.value.length },
  { id: 'future', label: 'Future Candidates', count: futureCandidates.value.length }
])

const futureCandidatesCount = computed(() => futureCandidates.value.length)

// Helpers
const formatDate = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const statusColor = (status) => {
  const colors = {
    new: 'text-blue-600',
    shortlisted: 'text-yellow-600',
    interview: 'text-purple-600',
    employed: 'text-green-600',
    rejected: 'text-red-600'
  }
  return colors[status] || 'text-gray-600'
}

const statusBadgeClass = (status) => {
  const classes = {
    new: 'bg-blue-100 text-blue-800',
    shortlisted: 'bg-yellow-100 text-yellow-800',
    interview: 'bg-purple-100 text-purple-800',
    employed: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}

const showToast = (message, type = 'success') => {
  toast.value = { message, type }
  setTimeout(() => { toast.value = null }, 3000)
}

let searchTimeout = null
const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => loadApplications(), 300)
}

// API calls
const loadApplications = async () => {
  loading.value = true
  try {
    const params = {}
    if (filterJob.value) params.jobId = filterJob.value
    if (filterStatus.value) params.status = filterStatus.value
    if (searchQuery.value) params.search = searchQuery.value

    const { data } = await api.get('/api/admin-applications', { params })
    applications.value = data.applications
    applicationStats.value = data.stats
  } catch (err) {
    showToast('Failed to load applications', 'error')
  } finally {
    loading.value = false
  }
}

const loadJobs = async () => {
  loadingJobs.value = true
  try {
    const { data } = await api.get('/api/admin-jobs')
    jobs.value = data.jobs
  } catch (err) {
    showToast('Failed to load jobs', 'error')
  } finally {
    loadingJobs.value = false
  }
}

const loadFutureCandidates = async () => {
  loadingFuture.value = true
  try {
    const { data } = await api.get('/api/admin-future-candidates')
    futureCandidates.value = data.candidates
  } catch (err) {
    showToast('Failed to load future candidates', 'error')
  } finally {
    loadingFuture.value = false
  }
}

const selectApplication = (app) => {
  selectedApp.value = { ...app }
}

const updateApplicationStatus = async (app, status) => {
  try {
    const { data } = await api.patch(`/api/admin-applications?id=${app._id}`, { status })
    selectedApp.value = data.application
    // Update in list too
    const idx = applications.value.findIndex(a => a._id === app._id)
    if (idx >= 0) applications.value[idx] = data.application
    showToast(`Status updated to ${status}`)
    loadApplications() // refresh stats
  } catch (err) {
    showToast('Failed to update status', 'error')
  }
}

const saveNotes = async (app) => {
  try {
    await api.patch(`/api/admin-applications?id=${app._id}`, { notes: app.notes })
  } catch (err) {
    showToast('Failed to save notes', 'error')
  }
}

const sendInterviewInvite = async () => {
  if (!selectedApp.value || sendingInterview.value) return
  sendingInterview.value = true
  try {
    const { data } = await api.post('/api/send-interview-email', {
      applicationId: selectedApp.value._id,
      interviewDate: interviewForm.value.date,
      interviewTime: interviewForm.value.time,
      interviewLocation: interviewForm.value.location,
      customMessage: interviewForm.value.customMessage
    })
    showToast(data.message)
    showInterviewModal.value = false
    interviewForm.value = { date: '', time: '', location: '', customMessage: '' }
    loadApplications()
    // Refresh selected app
    selectedApp.value.status = 'interview'
  } catch (err) {
    showToast('Failed to send interview email', 'error')
  } finally {
    sendingInterview.value = false
  }
}

const markEmployedAndClose = async (app) => {
  if (!confirm(`Mark ${app.name} as employed and send closing emails to all other candidates for "${app.jobTitle}"? This will invite them to opt-in for future job notifications.`)) return

  try {
    // Send closing emails to other candidates
    const { data } = await api.post('/api/send-closing-emails', {
      jobId: app.jobId,
      employedApplicationId: app._id
    })
    showToast(data.message)
    selectedApp.value = null
    loadApplications()
  } catch (err) {
    showToast('Failed to close position', 'error')
  }
}

// Manual application
const onManualJobSelect = () => {
  if (manualAppForm.value.jobId && manualAppForm.value.jobId !== '_other') {
    const job = jobs.value.find(j => j.jobId === manualAppForm.value.jobId)
    if (job) manualAppForm.value.jobTitle = job.title
  } else if (manualAppForm.value.jobId !== '_other') {
    manualAppForm.value.jobTitle = ''
  }
}

const submitManualApplication = async () => {
  if (savingManualApp.value) return
  savingManualApp.value = true
  try {
    const jobTitle = manualAppForm.value.jobId === '_other'
      ? manualAppForm.value.jobTitle
      : manualAppForm.value.jobTitle
    const jobId = manualAppForm.value.jobId === '_other' ? '' : manualAppForm.value.jobId

    await api.post('/api/admin-applications', {
      ...manualAppForm.value,
      jobTitle,
      jobId
    })
    showToast('Application added')
    showManualAppForm.value = false
    manualAppForm.value = { jobId: '', jobTitle: '', name: '', email: '', phone: '', location: '', languages: '', qualifications: '', licence: '', motivation: '', reference: '' }
    loadApplications()
  } catch (err) {
    showToast('Failed to add application', 'error')
  } finally {
    savingManualApp.value = false
  }
}

// Job management
const addSection = () => {
  jobForm.value.sections.push({ title: '', itemsText: '' })
}

const saveJob = async () => {
  savingJob.value = true
  try {
    const payload = {
      ...jobForm.value,
      sections: jobForm.value.sections.map(s => ({
        title: s.title,
        items: s.itemsText.split('\n').map(l => l.trim()).filter(Boolean)
      }))
    }

    if (editingJob.value) {
      await api.patch(`/api/admin-jobs?id=${editingJob.value._id}`, payload)
      showToast('Job updated')
    } else {
      const { data } = await api.post('/api/admin-jobs', payload)
      showToast(data.message)
    }
    showJobForm.value = false
    editingJob.value = null
    jobForm.value = { title: '', location: '', type: 'Full-time', deadline: '', summary: '', sections: [] }
    loadJobs()
  } catch (err) {
    showToast('Failed to save job', 'error')
  } finally {
    savingJob.value = false
  }
}

const editJob = (job) => {
  editingJob.value = job
  jobForm.value = {
    title: job.title,
    location: job.location,
    type: job.type,
    deadline: job.deadline,
    summary: job.summary,
    sections: (job.sections || []).map(s => ({ title: s.title, itemsText: (s.items || []).join('\n') }))
  }
  showJobForm.value = true
}

const toggleJobActive = async (job) => {
  try {
    await api.patch(`/api/admin-jobs?id=${job._id}`, { active: !job.active })
    showToast(job.active ? 'Job closed' : 'Job reopened')
    loadJobs()
  } catch (err) {
    showToast('Failed to update job', 'error')
  }
}

const removeFutureCandidate = async (candidate) => {
  if (!confirm(`Remove ${candidate.name} from future candidates list?`)) return
  try {
    await api.delete(`/api/admin-future-candidates?id=${candidate._id}`)
    showToast('Candidate removed')
    loadFutureCandidates()
  } catch (err) {
    showToast('Failed to remove candidate', 'error')
  }
}

// Load data on mount
onMounted(() => {
  loadApplications()
  loadJobs()
  loadFutureCandidates()
})
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fade-in 0.3s ease-out;
}
</style>
